import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../i18n/index.js";
import { requestScope } from "../api/scope.js";
import { onWs } from "../api/ws.js";
import { useSession } from "../state/session.js";
import { Icon } from "../ui/Icon.js";
import { Button } from "../ui/Button.js";
import { Timer, useServerTick } from "../ui/Timer.js";
import { baseApi } from "./api.js";
import type { AutoCollectorView } from "./loop-types.js";
import "./autocollector.css";

/** A server setting, not a browser collection loop. Safe to mount in both VIP shop and inventory. */
export function AutoCollectorControl() {
  const t = useT(), accountId = useSession((state) => state.account?.id), now = useServerTick(1);
  const vipLevel = useSession((state) => state.vip?.level ?? 0);
  const [state, setState] = useState<AutoCollectorView | null>(null), [busy, setBusy] = useState(false), [failed, setFailed] = useState(false);
  const ticket = useRef(0), lastRefresh = useRef<number | null>(null);
  const load = useCallback(async () => {
    const owner = ++ticket.current, generation = requestScope().generation;
    try { const next = await baseApi.autoCollector(); if (ticket.current === owner && requestScope().generation === generation) { setState(next); setFailed(false); } }
    catch { if (ticket.current === owner && requestScope().generation === generation) setFailed(true); }
  }, []);
  useEffect(() => {
    setState(null); setBusy(false); setFailed(false); lastRefresh.current = null;
    if (accountId) void load();
    return () => { ++ticket.current; };
  }, [accountId, vipLevel, load]);
  useEffect(() => onWs("balances.resources", (event) => {
    if ((event.payload as { reason?: string }).reason === "production.auto_collect") void load();
  }), [load]);
  useEffect(() => {
    if (state?.nextAt != null && now >= state.nextAt + 1500 && now - (lastRefresh.current ?? 0) >= 5000 && !busy) {
      lastRefresh.current = now; void load();
    }
  }, [state?.nextAt, now, busy, load]);
  if (!accountId) return null;
  const change = async () => {
    if (!state || busy || !state.unlocked) return;
    const owner = ++ticket.current, generation = requestScope().generation;
    setBusy(true); setFailed(false);
    try {
      const next = await baseApi.setAutoCollector(!state.enabled, state.revision);
      if (ticket.current === owner && requestScope().generation === generation) setState(next);
    } catch {
      if (ticket.current === owner && requestScope().generation === generation) { setFailed(true); void load(); }
    } finally { if (requestScope().generation === generation) setBusy(false); }
  };
  return <section className="autocollector-control" data-testid="auto-collector" data-state={!state ? "loading" : state.enabled ? "enabled" : "disabled"}>
    <div className="autocollector-heading"><strong><Icon name="refresh" /> {t("base.autoCollector.title")}</strong><span className="badge">VIP 3</span></div>
    <details className="field-details"><summary>Details</summary><p>{t("base.autoCollector.description")}</p></details>
    {!state ? failed ? <Button onClick={() => void load()}><Icon name="refresh" /> {t("store.retry")}</Button> : <p className="hint">{t("common.loading")}</p> : <>
      <button type="button" className="autocollector-switch" role="switch" aria-checked={state.enabled} aria-label={t("base.autoCollector.title")} disabled={busy || !state.unlocked} onClick={() => void change()} data-testid="auto-collector-switch"><span aria-hidden="true"><i /></span>{t(state.enabled ? "base.autoCollector.on" : "base.autoCollector.off")}</button>
      {!state.unlocked ? <p className="hint">{t("base.autoCollector.locked")}</p> : null}
      {state.enabled && state.nextAt !== null ? <p className="hint" data-testid="auto-collector-next">{t("base.autoCollector.next")} <Timer finishesAt={state.nextAt} /></p> : null}
      {state.lastAt !== null ? <p className="hint" data-testid="auto-collector-last">{t("base.autoCollector.last", { n: Object.values(state.lastCollected).reduce((sum, amount) => sum + amount, 0) })}</p> : null}
    </>}
    {failed && state ? <p role="alert">{t("base.autoCollector.failed")}</p> : null}
  </section>;
}
